import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Aperture, Loader2 } from "lucide-react";

const AUTH_IMG = "https://images.pexels.com/photos/19923619/pexels-photo-19923619.jpeg";

export default function Login() {
  const { login, user, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/studio" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/studio");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="photographer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Light shapes the story.</h2>
          <p className="text-neutral-300 mt-3 max-w-md">Dial in your lens and lighting. Let Aevolux compose the perfect shot.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <Aperture className="h-7 w-7 text-amber-glow" strokeWidth={1.5} />
            <span className="font-display text-2xl">Aevolux</span>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-500">Welcome back</p>
          <h1 className="font-display text-4xl mt-2 mb-8">Sign in</h1>

          {error && (
            <div data-testid="login-error" className="mb-5 text-sm text-red-400 bg-red-950/40 border border-red-900/50 px-4 py-3 rounded-sm">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-neutral-400">Email</label>
              <Input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className="mt-2 bg-[#121212] border-white/10 h-12 rounded-sm focus-visible:ring-amber-glow"
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-neutral-400">Password</label>
              <Input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                className="mt-2 bg-[#121212] border-white/10 h-12 rounded-sm focus-visible:ring-amber-glow"
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="w-full h-12 bg-white text-black font-semibold hover:bg-neutral-200 rounded-sm"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-neutral-400">
              New here?{" "}
              <Link to="/register" data-testid="goto-register" className="text-amber-glow hover:underline">Create an account</Link>
            </p>
            <Link to="/forgot-password" data-testid="goto-forgot" className="text-sm text-neutral-500 hover:text-amber-glow transition-colors">
              Forgot password?
            </Link>
          </div>

          <p className="text-xs text-neutral-600 mt-10">
            By continuing you agree to our{" "}
            <Link to="/privacy" data-testid="login-privacy-link" className="text-neutral-400 hover:text-amber-glow underline underline-offset-2">Privacy Policy</Link>{" "}and{" "}
            <Link to="/terms" data-testid="login-terms-link" className="text-neutral-400 hover:text-amber-glow underline underline-offset-2">Terms of Service</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
